import React from "react";
import { CheckCircle2, Loader2, Sparkles } from "lucide-react";
import {
  CLIENT_BILLING_TIERS,
  formatMonthlyPrice,
} from "@/constants/clientBillingTiers";

const BillingTierCards = ({
  onSelect,
  isSubmitting,
  submittingTier,
  errorMessage,
}) => (
  <div data-testid="billing-tier-cards">
    <div className="text-center mb-10">
      <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-3">
        Choose Your AI Employee Plan
      </h2>
      <p className="font-body text-sm md:text-base text-[#9CA3AF] max-w-2xl mx-auto leading-relaxed">
        All plans include CRM and calendar sync, instant lead qualification, and
        monthly AI credits for live conversations. No setup fee — subscribe and go live.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {CLIENT_BILLING_TIERS.map((tier) => {
        const isLoading = isSubmitting && submittingTier === tier.id;
        const isDisabled = isSubmitting && submittingTier !== tier.id;

        return (
          <div
            key={tier.id}
            className={`relative flex flex-col rounded-2xl border p-6 ${
              tier.recommended
                ? "border-[#00D1FF]/40 bg-[#00D1FF]/5 shadow-[0_0_40px_rgba(0,209,255,0.08)]"
                : "border-white/10 bg-white/[0.02]"
            }`}
            data-testid={`billing-tier-card-${tier.id}`}
          >
            {tier.recommended && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#00D1FF] px-3 py-1 text-[10px] font-heading font-bold uppercase tracking-wider text-[#0F1115]">
                Recommended
              </span>
            )}

            <div className="mb-4">
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#00D1FF] mb-1">
                {tier.badge}
              </p>
              <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                {tier.name}
              </h3>
            </div>

            <p className="font-heading font-bold text-3xl text-[#F3F4F6] mb-1">
              {formatMonthlyPrice(tier.monthlyPrice)}
            </p>
            <p className="text-xs text-[#9CA3AF] mb-6">Billed monthly · cancel anytime</p>

            <ul className="space-y-3 mb-8 flex-1">
              <li className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                <Sparkles className="h-4 w-4 text-[#00D1FF] flex-shrink-0 mt-0.5" />
                <span>{tier.creditsLabel}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                <CheckCircle2 className="h-4 w-4 text-[#00D1FF] flex-shrink-0 mt-0.5" />
                <span>{tier.capacity}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-[#9CA3AF]">
                <CheckCircle2 className="h-4 w-4 text-[#00D1FF] flex-shrink-0 mt-0.5" />
                <span>{tier.systems}</span>
              </li>
            </ul>

            <button
              type="button"
              onClick={() => onSelect(tier.id)}
              disabled={isDisabled || isLoading}
              className={`w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 font-heading font-bold text-sm transition-colors duration-300 ${
                isDisabled || isLoading
                  ? "bg-[#00D1FF]/40 text-[#0F1115]/60 cursor-not-allowed"
                  : tier.recommended
                  ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                  : "bg-white/10 text-[#F3F4F6] hover:bg-white/15"
              }`}
              data-testid={`billing-subscribe-tier-${tier.id}`}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Redirecting to Stripe…
                </>
              ) : (
                <>Subscribe — {formatMonthlyPrice(tier.monthlyPrice)}</>
              )}
            </button>
          </div>
        );
      })}
    </div>

    {errorMessage && (
      <p
        className="mt-6 text-sm text-red-400 text-center"
        data-testid="billing-error-message"
        role="alert"
      >
        {errorMessage}
      </p>
    )}
  </div>
);

export default BillingTierCards;
